import { useEffect, useState } from "react";
import { Card, DatePicker, Input, Table, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import dayjs from "dayjs";
import { postReq } from "../api";

const statusStyle = (value) => ({
  background: value === "A" ? "#fdecea" : "transparent",
  color: value === "A" ? "#d32f2f" : "inherit",
  borderRadius: "50%",
  width: 28,
  height: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 600,
  fontSize: 12,
  margin: "auto",
});

const generateDayColumns = (days) =>
  Array.from({ length: days }, (_, i) => ({
    title: `${i + 1}`,
    dataIndex: `day${i + 1}`,
    key: `day${i + 1}`,
    width: 36,
    align: "center",
    render: (value) => <div style={statusStyle(value)}>{value}</div>,
  }));

const AttendanceTable = () => {
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });

  const daysInMonth = selectedMonth.daysInMonth();

  const columns = [
    {
      title: "Employee Name",
      dataIndex: "name",
      key: "name",
      width: 170,
      fixed: "left",
    },
    ...generateDayColumns(daysInMonth),
    { title: "P", dataIndex: "P", key: "P", width: 50, align: "center" },
    { title: "H", dataIndex: "H", key: "H", width: 50, align: "center" },
    { title: "A", dataIndex: "A", key: "A", width: 50, align: "center" },
    { title: "PH", dataIndex: "PH", key: "PH", width: 50, align: "center" },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      width: 60,
      align: "center",
    },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await postReq("/user/attendance_records", {
        page: pagination.page,
        pageSize: pagination.pageSize,
        search,
        month: selectedMonth.format("M"),
        year: selectedMonth.format("YYYY"),
      });
      if (res.status === 200) {
        setRows(res.data.rows);
        setTotal(res.data.total);
      } else {
        message.error(res.message || "Error fetching attendance");
        setRows([]);
        setTotal(0);
      }
    } catch (error) {
      message.error(error.message || "Error fetching attendance");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth, pagination, search]);

  return (
    <Card
      bordered={false}
      styles={{ body: { padding: "24px 40px" } }}
      style={{ flex: 1 }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        <Input
          placeholder="Search User Name"
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 240 }}
        />

        <DatePicker
          picker="month"
          value={selectedMonth}
          disabledDate={(d) => d && d.isAfter(dayjs(), "month")}
          onChange={(val) => val && setSelectedMonth(val)}
          format="MMM YYYY"
          style={{ width: 160 }}
          allowClear={false}
        />
      </div>

      {/* Attendance Table */}
      <Table
        loading={loading}
        dataSource={rows}
        columns={columns}
        rowKey={(row) => row._id}
        scroll={{ x: "max-content" }}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "20"],
          showTotal: (tot) => `Total ${tot} records`,
          onChange: (page, pageSize) => setPagination({ page, pageSize }),
        }}
      />
    </Card>
  );
};

export default AttendanceTable;
